import { useEffect, useMemo, useState } from "react";
import { Users, Calendar, Link2, BarChart3, Target, ArrowUp, ArrowDown, Search, Download, X, Mail, Phone, CalendarPlus, Send, CheckCircle, ChevronDown, Loader2, AlertTriangle, Archive, ArchiveRestore, Trash2, MessageSquare, Package } from "lucide-react";
import { LEAD_STATUS_META, PRIO_META, LEAD_STAGE_ORDER, formatKr, formatInt } from "@/lib/adminMockData";
import { fetchAdminLeads, updateAdminLead, deleteAdminLead, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SORT_OPTIONS = [
    { id: "newest", label: "Nyeste først" },
    { id: "oldest", label: "Ældste først" },
    { id: "value_desc", label: "Højeste værdi" },
    { id: "value_asc", label: "Laveste værdi" },
    { id: "name_asc", label: "Navn A→Å" },
    { id: "status", label: "Status (pipeline)" },
];

function StatCard({ icon: Icon, label, value, growthLabel, growthPositive = true, suffix, testid }) {
    const hasGrowth = growthLabel !== null && growthLabel !== undefined;
    return (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4 shadow-sm" data-testid={testid}>
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-2">
                <div className="w-7 h-7 rounded-lg bg-slate-50 inline-flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-slate-500" /></div>
                {label}
            </div>
            <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-slate-900">{value}{suffix}</p>
                {hasGrowth && (
                    <span className={`text-xs font-semibold inline-flex items-center gap-0.5 ${growthPositive ? "text-emerald-600" : "text-red-500"}`}>
                        {growthPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{growthLabel}
                    </span>
                )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">vs. sidste 30 dage</p>
        </div>
    );
}

function ConfirmDeleteModal({ lead, onCancel, onConfirm, busy }) {
    if (!lead) return null;
    return (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lead-delete-confirm">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-6">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-red-50 inline-flex items-center justify-center shrink-0"><Trash2 className="w-5 h-5 text-red-600" /></div>
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">Slet lead permanent?</h3>
                        <p className="text-sm text-slate-500 mt-1">Dette kan ikke fortrydes. Leadet bliver fjernet fra både demo-anmodninger og pipeline-data.</p>
                    </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 mb-5">
                    <p className="text-sm font-medium text-slate-900">{lead.name}</p>
                    <p className="text-xs text-slate-500">{lead.company} · {lead.email}</p>
                </div>
                <div className="flex gap-2 justify-end">
                    <button onClick={onCancel} disabled={busy} data-testid="lead-delete-cancel" className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100">Annullér</button>
                    <button onClick={onConfirm} disabled={busy} data-testid="lead-delete-confirm-btn" className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-60 inline-flex items-center gap-1.5">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Slet permanent
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminLeads() {
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState("");
    const [updatingId, setUpdatingId] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [sort, setSort] = useState("newest");
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const reload = async (opts = {}) => {
        try {
            setLoading(true);
            setError("");
            const params = {
                archived: opts.archived ?? showArchived,
                sort: opts.sort ?? sort,
            };
            const d = await fetchAdminLeads(params);
            setPayload(d);
            // Keep selection if still visible; else select first
            if (d.leads?.length) {
                const stillThere = selected ? d.leads.find((l) => l.id === selected.id) : null;
                setSelected(stillThere || d.leads[0]);
            } else {
                setSelected(null);
            }
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload({ archived: showArchived, sort }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const leads = payload?.leads || [];
    const stages = payload?.pipeline_stages || [];
    const kpis = payload?.kpis || {};

    const filtered = useMemo(() => {
        if (!search) return leads;
        const q = search.toLowerCase();
        return leads.filter((l) => [l.name, l.company, l.industry, l.source, l.package, l.email].some((v) => (v || "").toLowerCase().includes(q)));
    }, [leads, search]);

    const handleArchivedToggle = (next) => {
        setShowArchived(next);
        reload({ archived: next, sort });
    };
    const handleSortChange = (next) => {
        setSort(next);
        reload({ archived: showArchived, sort: next });
    };

    const patchLead = async (leadId, body, successMsg) => {
        try {
            setUpdatingId(leadId);
            await updateAdminLead(leadId, body);
            if (successMsg) toast.success(successMsg);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUpdatingId(null);
        }
    };

    const handleStatusChange = (leadId, newStatus) => patchLead(leadId, { status: newStatus }, "Status opdateret");
    const handlePriorityChange = (leadId, newPriority) => patchLead(leadId, { priority: newPriority }, "Prioritet opdateret");
    const handleArchive = (lead) => patchLead(lead.id, { archived: !lead.archived }, lead.archived ? "Lead genaktiveret" : "Lead arkiveret");

    const handleDelete = async () => {
        if (!pendingDelete) return;
        try {
            setDeleting(true);
            await deleteAdminLead(pendingDelete.id);
            toast.success("Lead slettet permanent");
            setPendingDelete(null);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    };

    if (loading && !payload) {
        return (
            <div className="flex items-center justify-center py-24" data-testid="admin-leads-loading">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
        );
    }
    if (error && !payload) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3" data-testid="admin-leads-error">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                    <p className="font-semibold text-red-800">Kunne ikke hente leads</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-leads">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-slate-900">Leads</h1>
                <p className="text-sm text-slate-500 mt-1">Få overblik over alle demo- og kontaktleads i din pipeline (live data).</p>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                <StatCard icon={Users} label="Nye leads (30d)" value={formatInt(kpis.new_leads)} growthLabel={`${Math.round((kpis.new_leads_growth || 0) * 100)}%`} growthPositive={(kpis.new_leads_growth || 0) >= 0} testid="leads-kpi-new" />
                <StatCard icon={Calendar} label="Bookede demoer" value={formatInt(kpis.booked_demos)} growthLabel={kpis.booked_demos_growth == null ? null : `${Math.round(kpis.booked_demos_growth * 100)}%`} testid="leads-kpi-demos" />
                <StatCard icon={Link2} label="SQL" value={formatInt(kpis.sql)} growthLabel={kpis.sql_growth == null ? null : `${Math.round(kpis.sql_growth * 100)}%`} testid="leads-kpi-sql" />
                <StatCard icon={BarChart3} label="Win rate" value={`${Math.round((kpis.win_rate || 0) * 100)}`} suffix="%" growthLabel={kpis.win_rate_change_pp == null ? null : `${kpis.win_rate_change_pp} p.p.`} testid="leads-kpi-winrate" />
                <StatCard icon={Target} label="Pipeline værdi" value={formatKr(kpis.pipeline_value)} growthLabel={kpis.pipeline_value_growth == null ? null : `${Math.round(kpis.pipeline_value_growth * 100)}%`} testid="leads-kpi-pipeline" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    {/* Pipeline summary */}
                    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="lead-pipeline">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <div>
                                <h2 className="text-base font-semibold text-slate-900 inline">Lead pipeline</h2>
                                <span className="text-xs text-slate-400 ml-2">{kpis.active_count || 0} aktive · {kpis.archived_count || 0} arkiverede</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                            {stages.map((s) => (
                                <div key={s.id} className="text-center" data-testid={`pipeline-${s.id}`}>
                                    <div className="h-1 rounded-t-full" style={{ backgroundColor: s.color }} />
                                    <div className="border-x border-b border-slate-100 rounded-b-lg p-3">
                                        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: s.color }}>{s.name}</p>
                                        <p className="text-2xl font-bold text-slate-900 my-1">{s.count}</p>
                                        <p className="text-[11px] text-slate-500">{(s.share ?? 0).toString().replace(".", ",")}%</p>
                                        <p className="text-[11px] text-slate-400 mt-1">Værdi {formatKr(s.mrr)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Leads table */}
                    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5" data-testid="leads-table-wrap">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="leads-archive-toggle">
                                <button onClick={() => handleArchivedToggle(false)} data-testid="leads-tab-active" className={`text-xs px-3 py-1.5 rounded-md font-medium transition ${!showArchived ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Aktive ({kpis.active_count || 0})</button>
                                <button onClick={() => handleArchivedToggle(true)} data-testid="leads-tab-archived" className={`text-xs px-3 py-1.5 rounded-md font-medium transition inline-flex items-center gap-1 ${showArchived ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}><Archive className="w-3 h-3" />Arkiverede ({kpis.archived_count || 0})</button>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <div className="relative">
                                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input data-testid="leads-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søg leads…" className="text-xs border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 bg-white w-44 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                                </div>
                                <select data-testid="leads-sort" value={sort} onChange={(e) => handleSortChange(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400">
                                    {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                </select>
                                <button className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white inline-flex items-center gap-1.5"><Download className="w-3 h-3" />Eksportér</button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="leads-table">
                                <thead>
                                    <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                                        <th className="py-2 text-left font-medium">Lead</th>
                                        <th className="py-2 text-left font-medium">Virksomhed</th>
                                        <th className="py-2 text-left font-medium">Kilde</th>
                                        <th className="py-2 text-left font-medium">Pakke + ekstra</th>
                                        <th className="py-2 text-left font-medium">Status</th>
                                        <th className="py-2 text-left font-medium">Prioritet</th>
                                        <th className="py-2 text-right font-medium">Værdi</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filtered.length === 0 ? (
                                        <tr><td colSpan={7} className="py-10 text-center text-sm text-slate-400">{showArchived ? "Ingen arkiverede leads." : "Ingen aktive leads — demo- og kontakt-formularer ender her."}</td></tr>
                                    ) : filtered.map((l) => {
                                        const status = LEAD_STATUS_META[l.status] || LEAD_STATUS_META.ny;
                                        const prio = PRIO_META[l.priority] || PRIO_META.medium;
                                        const isSel = selected?.id === l.id;
                                        return (
                                            <tr key={l.id} onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}
                                                className={`cursor-pointer transition ${isSel ? "bg-blue-50/40" : "hover:bg-slate-50"} ${l.archived ? "opacity-60" : ""}`}>
                                                <td className="py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ backgroundColor: l.initial_bg }}>{l.initial}</div>
                                                        <div>
                                                            <p className="font-medium text-slate-900 inline-flex items-center gap-1.5">{l.name}{l.archived && <Archive className="w-3 h-3 text-slate-400" />}</p>
                                                            <p className="text-[11px] text-slate-500">{l.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 text-slate-600">{l.company}</td>
                                                <td className="py-3 text-slate-600">{l.source}</td>
                                                <td className="py-3 text-slate-600 max-w-[260px]"><span className="line-clamp-1" title={l.package}>{l.package}</span></td>
                                                <td className="py-3"><span className={`text-[11px] px-2 py-0.5 rounded-md border ${status.bg} ${status.text} ${status.border}`}>{status.label}</span></td>
                                                <td className="py-3"><span className={`text-[11px] px-2 py-0.5 rounded-md ${prio.bg} ${prio.text}`}>{prio.label}</span></td>
                                                <td className="py-3 text-right font-medium text-slate-900">{formatKr(l.value)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Right detail panel */}
                <div className="lg:col-span-1">
                    {selected && (
                        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 lg:sticky lg:top-6" data-testid="lead-detail">
                            <div className="flex items-start justify-between mb-3">
                                <div className="min-w-0">
                                    <h3 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2 flex-wrap">{selected.name}{selected.archived && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-semibold uppercase">Arkiveret</span>}</h3>
                                    <p className="text-sm text-slate-500">{selected.company}</p>
                                </div>
                                <button onClick={() => setSelected(null)} data-testid="lead-detail-close"><X className="w-4 h-4 text-slate-400" /></button>
                            </div>

                            <div className="mb-5">
                                <p className="text-xs text-slate-500 font-semibold uppercase mb-2">Kontaktoplysninger</p>
                                <div className="space-y-1.5 text-sm">
                                    {selected.email && <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600"><Mail className="w-3.5 h-3.5 text-slate-400" />{selected.email}</a>}
                                    {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-600"><Phone className="w-3.5 h-3.5 text-slate-400" />{selected.phone}</a>}
                                </div>
                            </div>

                            <div className="mb-5">
                                <p className="text-xs text-slate-500 font-semibold uppercase mb-2 inline-flex items-center gap-1.5"><Package className="w-3 h-3" />Kundens valg</p>
                                <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Pakke</span>
                                        <span className="font-medium text-slate-900">{selected.selection?.plan_label || "—"}</span>
                                    </div>
                                    {(selected.selection?.addons || []).map((a) => (
                                        <div key={a.id} className="flex items-center justify-between text-xs">
                                            <span className="text-slate-500">{a.qty}× {a.name}</span>
                                            <span className="text-slate-700">{formatKr(a.monthly)}</span>
                                        </div>
                                    ))}
                                    {(selected.selection?.monthly || 0) > 0 && (
                                        <div className="flex items-center justify-between pt-1.5 border-t border-slate-200">
                                            <span className="text-xs font-semibold text-slate-700">Estimeret / md</span>
                                            <span className="text-sm font-bold text-violet-700">{formatKr(selected.selection.monthly)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selected.general_question && (
                                <div className="mb-5">
                                    <p className="text-xs text-slate-500 font-semibold uppercase mb-2 inline-flex items-center gap-1.5"><MessageSquare className="w-3 h-3" />Generelle spørgsmål</p>
                                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-slate-800 whitespace-pre-line max-h-40 overflow-y-auto" data-testid="lead-general-question">{selected.general_question}</div>
                                </div>
                            )}

                            <div className="mb-5">
                                <p className="text-xs text-slate-500 font-semibold uppercase mb-2">Status</p>
                                <select
                                    value={selected.status}
                                    onChange={(e) => handleStatusChange(selected.id, e.target.value)}
                                    disabled={updatingId === selected.id || selected.archived}
                                    data-testid="lead-status-select"
                                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
                                >
                                    {LEAD_STAGE_ORDER.map((s) => (
                                        <option key={s} value={s}>{LEAD_STATUS_META[s].label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-5">
                                <p className="text-xs text-slate-500 font-semibold uppercase mb-2">Prioritet</p>
                                <div className="grid grid-cols-3 gap-2">
                                    {["high", "medium", "low"].map((p) => {
                                        const meta = PRIO_META[p];
                                        const active = selected.priority === p;
                                        return (
                                            <button
                                                key={p}
                                                onClick={() => handlePriorityChange(selected.id, p)}
                                                disabled={updatingId === selected.id || selected.archived}
                                                data-testid={`lead-priority-${p}`}
                                                className={`text-xs px-2 py-1.5 rounded-md transition ${active ? `${meta.bg} ${meta.text} ring-2 ring-offset-1 ring-blue-400` : "bg-slate-50 text-slate-500 hover:bg-slate-100"} disabled:opacity-60`}
                                            >
                                                {meta.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {!selected.archived && (
                                <div className="mb-5">
                                    <p className="text-xs text-slate-500 font-semibold uppercase mb-2">Hurtige handlinger</p>
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => handleStatusChange(selected.id, "demo_booket")}
                                            disabled={updatingId === selected.id}
                                            className="w-full h-10 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                            data-testid="lead-action-book"
                                        ><CalendarPlus className="w-4 h-4" />Marker som demo-booket</button>
                                        <button
                                            onClick={() => handleStatusChange(selected.id, "tilbud_sendt")}
                                            disabled={updatingId === selected.id}
                                            className="w-full h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                            data-testid="lead-action-payment-link"
                                        ><Send className="w-4 h-4" />Marker som tilbud sendt</button>
                                        <button
                                            onClick={() => handleStatusChange(selected.id, "vundet")}
                                            disabled={updatingId === selected.id}
                                            className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                            data-testid="lead-action-won"
                                        ><CheckCircle className="w-4 h-4" />Marker som vundet</button>
                                    </div>
                                </div>
                            )}

                            <div className="border-t border-slate-100 pt-4">
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => handleArchive(selected)}
                                        disabled={updatingId === selected.id}
                                        data-testid="lead-archive-btn"
                                        className="h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                    >
                                        {selected.archived ? <><ArchiveRestore className="w-4 h-4" />Genaktiver</> : <><Archive className="w-4 h-4" />Arkivér</>}
                                    </button>
                                    <button
                                        onClick={() => setPendingDelete(selected)}
                                        disabled={updatingId === selected.id}
                                        data-testid="lead-delete-btn"
                                        className="h-10 rounded-lg border border-red-200 hover:bg-red-50 text-red-600 text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                    >
                                        <Trash2 className="w-4 h-4" />Slet
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDeleteModal
                lead={pendingDelete}
                busy={deleting}
                onCancel={() => setPendingDelete(null)}
                onConfirm={handleDelete}
            />
        </div>
    );
}
